import apiClient from './apiClient';
import { jwtDecode } from 'jwt-decode';
import { UserType } from '../../common/data/dummyRolesData';
import { FeatureWithUsage } from '../../type/feature-types';
import { OrganizationMembership } from '../../type/organization-types';

// Token keys for storage
const ID_TOKEN_KEY = 'opentalents_id_token';
const ACCESS_TOKEN_KEY = 'opentalents_access_token';

// Default safety margin for token refresh (5 minutes in milliseconds)
const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

// User sync response interface - enhanced with features and organization memberships
export interface IUserSyncResponse {
	id: string;
	email: string;
	planType?: string | null;
	features?: FeatureWithUsage[];
	organizations?: OrganizationMembership[];
}

// User profile interface for extracted token data
export interface IUserProfile {
	id: string;
	email: string;
	name?: string;
	surname?: string;
	username?: string;
	role?: UserType;
}

// Auth service for handling authentication and user sync API
const authService = {
	/**
	 * Syncs user data with the backend
	 */
	syncUser: async (): Promise<IUserSyncResponse> => {
		try {
			// API call to sync user data
			const response = await apiClient.post('/api/auth/sync', {});
			return response.data;
		} catch (error: any) {
			// Handle timeout errors specifically
			if (error.code === 'ECONNABORTED') {
				// const idToken = authService.getIdToken();
				const accessToken = authService.getAccessToken();
				if (!accessToken) {
					throw new Error('Access token is missing');
				}

				const profile = authService.extractUserProfileFromAccessToken(accessToken);
				if (profile) {
					return {
						id: profile.id,
						email: profile.email,
					};
				}
			}
			throw error;
		}
	},

	/**
	 * Stores ID token in localStorage
	 */
	setIdToken: (token: string): void => {
		localStorage.setItem(ID_TOKEN_KEY, token);
	},

	/**
	 * Gets ID token from localStorage
	 */
	getIdToken: (): string => {
		return localStorage.getItem(ID_TOKEN_KEY) || '';
	},

	/**
	 * Stores access token in localStorage
	 */
	setAccessToken: (token: string): void => {
		localStorage.setItem(ACCESS_TOKEN_KEY, token);
	},

	/**
	 * Gets access token from localStorage
	 */
	getAccessToken: (): string => {
		return localStorage.getItem(ACCESS_TOKEN_KEY) || '';
	},

	/**
	 * Backward compatibility method - returns ID token
	 */
	getToken: (): string => {
		return localStorage.getItem(ID_TOKEN_KEY) || '';
	},

	/**
	 * Backward compatibility method - sets ID token
	 */
	setToken: (token: string): void => {
		localStorage.setItem(ID_TOKEN_KEY, token);
	},

	/**
	 * Extract user profile from ID token
	 */
	extractUserProfileFromIdToken: (idToken: string): IUserProfile | null => {
		try {
			const decoded: any = jwtDecode(idToken);
			return {
				id: decoded.sub,
				email: decoded.email,
				name: decoded.given_name,
				surname: decoded.family_name,
				username: decoded.given_name,
				role: decoded.UserType as UserType,
			};
		} catch (error) {
			console.error('Error extracting profile from ID token:', error);
			return null;
		}
	},

	/**
	 * Extract user profile from access token
	 */
	extractUserProfileFromAccessToken: (accessToken: string): IUserProfile | null => {
		try {
			const decoded: any = jwtDecode(accessToken);
			return {
				id: decoded.sub,
				email: decoded.email,
				name: decoded.given_name,
				surname: decoded.family_name,
				username: decoded.given_name,
				role: decoded.UserType as UserType,
			};
		} catch (error) {
			console.error('Error extracting profile from access token:', error);
			return null;
		}
	},

	/**
	 * Checks if token is valid and not expired
	 * @param token The token to validate
	 * @param withMargin Whether to include a safety margin before expiration
	 */
	isTokenValid: (token: string, withMargin = false): boolean => {
		try {
			if (!token) return false;
			const decoded: any = jwtDecode(token);
			if (!decoded.exp) return false;

			const currentTime = Date.now() / 1000;
			const margin = withMargin ? TOKEN_REFRESH_MARGIN_MS / 1000 : 0;
			return decoded.exp > currentTime + margin;
		} catch (error) {
			console.error('Error checking token validity:', error);
			return false;
		}
	},

	/**
	 * Gets token expiration time in milliseconds
	 */
	getTokenExpiration: (token: string): number | null => {
		try {
			const decoded: any = jwtDecode(token);
			return decoded.exp * 1000; // Convert to milliseconds
		} catch {
			return null;
		}
	},

	/**
	 * Clear all auth data from localStorage
	 */
	clearAuth: (): void => {
		localStorage.removeItem(ID_TOKEN_KEY);
		localStorage.removeItem(ACCESS_TOKEN_KEY);

		// Clear all MSAL-related items from localStorage
		Object.keys(localStorage)
			.filter(
				(key) =>
					key.includes('msal.') ||
					key.includes('opentalents_auth_') ||
					key.startsWith('msal'),
			)
			.forEach((key) => localStorage.removeItem(key));
	},

	/**
	 * Checks if the user is authenticated
	 * @param withMargin Check if authenticated with margin before token expiration
	 */
	isAuthenticated: (withMargin = false): boolean => {
		const idToken = authService.getIdToken();
		return !!idToken && authService.isTokenValid(idToken, withMargin);
	},

	/**
	 * Gets time until token expiration in milliseconds
	 * Returns 0 if token is expired or invalid
	 */
	getTimeUntilExpiration: (token: string): number => {
		try {
			const decoded: any = jwtDecode(token);
			if (!decoded.exp) return 0;

			const expiresAtMs = decoded.exp * 1000;
			const timeRemainingMs = expiresAtMs - Date.now();

			return timeRemainingMs > 0 ? timeRemainingMs : 0;
		} catch (error) {
			console.error('Error calculating token expiration:', error);
			return 0;
		}
	},
};

export default authService;
